// The confidence strip: a band under the ruler saying how much to trust the part
// of the timeline you are looking at.
//
// The stack was deliberately extended to seven days so a window can be planned
// rather than merely checked. That is only defensible if the screen is honest
// about the cost: a 156-hour forecast is not the same object as a 12-hour one,
// and drawing them as one continuous line implies it is. Mariners know weather
// gets less certain with range; what they cannot know from the line alone is
// WHERE this particular product's character changes.
//
// The boundaries are the model's, not a designer's:
//
//   past .. now   analysis — the run has already been corrected against observations
//   now .. +48    the ensemble band on the wind track is meaningful here
//   +48 .. +120   still hourly, but the spread widens faster than the line moves
//   +120 .. end   GFS-Wave drops to 3-hourly steps, so the field itself is coarser
//
// The last one is the one worth showing: it is a change in the DATA, not a change
// in our confidence about it, and nothing else on screen reveals it.

import { setCursor, timeAtIndex, timeOfLabel } from "../time/cursor.js";

/** Hours from now at which the character changes. */
const ENSEMBLE_TO = 48;
const HOURLY_TO = 120;

export interface ConfBarOptions {
  /** Must match the tracks' `gutterLeft`, or the bands will not line up with the
   *  hours they describe. */
  gutterLeft: number;
  /** Matches the charts' right margin. */
  gutterRight?: number;
  /**
   * This product's own boundaries. Defaults to the fleet console's, above.
   *
   * Added because the header of this file is right — "the boundaries are the
   * model's, not a designer's" — and a second console has a second model. The
   * thermal console's character changes at ITS measured blind error by lead, not
   * at GFS-Wave's 3-hourly step, and printing this file's default notes over
   * there would state something false about that product.
   *
   * Omitted, everything behaves exactly as before.
   */
  bands?: BandDef[];
}

/** One boundary: everything from the previous edge out to `toHours` past now. */
export interface BandDef {
  cls: string;
  label: string;
  note: string;
  toHours: number;
}

export interface ConfBar {
  /** Point the bar at a forecast's hours. `null` hides it. */
  update(xLabels: readonly string[] | null): void;
  destroy(): void;
}

interface Band {
  cls: string;
  /** Short enough to survive a narrow column — a label truncated mid-word is
   *  worse than a terse one. The meaning goes in `note`, shown on hover. */
  label: string;
  note: string;
  hours: number;
}

/** Split a span into the bands that fall inside it, dropping empty ones.
 *  Exported for the harness: the arithmetic is the whole substance here. */
export function bandsFor(
  firstMs: number,
  lastMs: number,
  nowMs: number,
  custom?: BandDef[],
): Band[] {
  const H = 3_600_000;
  if (custom !== undefined && custom.length > 0) {
    const out: Band[] = [];
    let lo = firstMs;
    for (const d of custom) {
      const hi = Math.max(firstMs, Math.min(lastMs, nowMs + d.toHours * H));
      const hours = (hi - lo) / H;
      if (hours > 0.01) {
        out.push({ cls: d.cls, label: d.label, note: d.note, hours });
      }
      lo = hi;
    }
    return out;
  }
  const edges = [firstMs, nowMs, nowMs + ENSEMBLE_TO * H, nowMs + HOURLY_TO * H, lastMs];
  const defs: { cls: string; label: string; note: string }[] = [
    { cls: "c0", label: "past", note: "Analysis — already corrected against observations" },
    {
      cls: "c1",
      label: `0–${ENSEMBLE_TO} h`,
      note: "Ensemble range is meaningful here; the band on the wind track is worth reading",
    },
    {
      cls: "c2",
      label: `${ENSEMBLE_TO}–${HOURLY_TO} h`,
      note: "Still hourly, but the member spread widens faster than the central line moves",
    },
    {
      cls: "c3",
      label: `${HOURLY_TO} h+`,
      note: "GFS-Wave drops to 3-hourly steps — the field itself is coarser out here, not just less certain",
    },
  ];
  const out: Band[] = [];
  for (let i = 0; i < defs.length; i++) {
    // Clamp each band to the span actually on screen. A console opened on a
    // 72-hour series must not draw a "120 h+" band it has no samples for.
    const lo = Math.max(firstMs, Math.min(lastMs, edges[i] ?? firstMs));
    const hi = Math.max(firstMs, Math.min(lastMs, edges[i + 1] ?? lastMs));
    const hours = (hi - lo) / H;
    if (hours > 0.01) out.push({ ...defs[i], hours } as Band);
  }
  return out;
}

export function createConfBar(host: HTMLElement, opts: ConfBarOptions): ConfBar {
  const root = document.createElement("div");
  root.className = "confbar";
  const gutterRight = opts.gutterRight ?? 10;
  root.style.paddingLeft = `${opts.gutterLeft}px`;
  root.style.paddingRight = `${gutterRight}px`;
  host.appendChild(root);

  // --- Scrubbing ------------------------------------------------------------
  //
  // The strip already pads itself with the tracks' own gutters so a band's edge
  // sits over the hour it describes — which means its content box is on exactly
  // the ruler's x-scale, and it can seek without duplicating any geometry.
  //
  // It scrubs for the same reason it is taller than it needs to be: the ruler
  // alone was a 30px target, and together these are one band you can grab
  // without aiming for it.
  let labels: readonly string[] = [];

  function seekTo(clientX: number): void {
    const rect = root.getBoundingClientRect();
    const left = rect.left + opts.gutterLeft;
    const right = rect.right - gutterRight;
    const n = labels.length;
    if (right <= left || n === 0) return;
    const denom = n > 1 ? n - 1 : 1;
    const raw = Math.round(((clientX - left) / (right - left)) * denom);
    const i = raw < 0 ? 0 : raw > n - 1 ? n - 1 : raw;
    setCursor(timeAtIndex(labels, i));
  }

  let scrubbing = false;
  function onDown(e: PointerEvent): void {
    scrubbing = true;
    root.setPointerCapture(e.pointerId);
    seekTo(e.clientX);
    e.preventDefault();
  }
  function onMove(e: PointerEvent): void {
    if (scrubbing) seekTo(e.clientX);
  }
  function onUp(e: PointerEvent): void {
    if (!scrubbing) return;
    scrubbing = false;
    if (root.hasPointerCapture(e.pointerId)) root.releasePointerCapture(e.pointerId);
  }
  root.addEventListener("pointerdown", onDown);
  root.addEventListener("pointermove", onMove);
  root.addEventListener("pointerup", onUp);
  root.addEventListener("pointercancel", onUp);

  return {
    update(xLabels): void {
      labels = xLabels ?? [];
      if (xLabels === null || xLabels.length < 2) {
        root.hidden = true;
        root.replaceChildren();
        return;
      }
      const first = timeOfLabel(xLabels[0] ?? "");
      const last = timeOfLabel(xLabels[xLabels.length - 1] ?? "");
      if (!Number.isFinite(first) || !Number.isFinite(last) || last <= first) {
        root.hidden = true;
        return;
      }
      root.hidden = false;
      const bands = bandsFor(first, last, Date.now(), opts.bands);
      root.replaceChildren(
        ...bands.map((b) => {
          const s = document.createElement("span");
          s.className = `cb ${b.cls}`;
          // flex-grow by hours, so a band's width IS its share of the timeline —
          // the same mapping the charts use, or the strip would lie about where
          // the boundary falls.
          s.style.flex = `${b.hours} 1 0`;
          s.textContent = b.label;
          s.title = `${b.label} (${Math.round(b.hours)} h) — ${b.note}`;
          return s;
        }),
      );
    },
    destroy(): void {
      root.removeEventListener("pointerdown", onDown);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerup", onUp);
      root.removeEventListener("pointercancel", onUp);
      root.remove();
    },
  };
}
